import React from 'react';
import { useNavigate } from 'react-router-dom';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';

import Markdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { useTrackedFormik } from '@omnigenbiodata/ui';

import MainLayout from '../../../../layout/Main';
import ROUTES from '../../../../core/constants/routes.constant';
import CONTENT from '../../../../core/constants/content.constant';
import { chartColours } from '../../../../core/constants/colours.constants';
import PageContainer from '../../../../components/content/PageContainer';
import PageSection from '../../../../components/content/PageSection';

function TermsScene() {
  const { t } = useTranslation('terms');
  const navigate = useNavigate();

  const formik = useTrackedFormik(
    {
      initialValues: { agree: false },
      onSubmit: () => {
        navigate(ROUTES.consentPrivacy);
      },
    },
    'consent-terms',
  );

  return (
    <MainLayout>
      <PageContainer
        chip={
          <Chip
            label={t('company')}
            size="small"
            sx={{
              mb: 1,
              fontWeight: 900,
              bgcolor: `${chartColours[1]}22`,
              color: chartColours[1],
              border: 1,
              borderColor: `${chartColours[1]}55`,
            }}
          />
        }
        title={t('title')}
        blurb={t('subtitle')}
        icon={
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${chartColours[0]}26`,
              color: chartColours[0],
              flexShrink: 0,
              border: 1,
              borderColor: `${chartColours[0]}55`,
            }}
          >
            <ArticleOutlined sx={{ fontSize: 34 }} />
          </Box>
        }
      >
        <form onSubmit={formik.handleSubmit}>
          {[...Array(23)].map((_, i) => {
            const sectionColour = chartColours[i % chartColours.length];

            return (
              <PageSection title={t(`section${i + 1}.title`)} colour={sectionColour} icon={i + 1} key={`term-${i}`}>
                <Typography variant="body2" component="div" color="text.secondary">
                  <Markdown>
                    {t(`section${i + 1}.content`, {
                      webAddress: CONTENT.MICROSITE_URL,
                      studyEmail: CONTENT.STUDY_EMAIL,
                      studyTelephone: CONTENT.STUDY_TEL,
                      termsEmail: CONTENT.TERMS_EMAIL,
                    })}
                  </Markdown>
                </Typography>
              </PageSection>
            );
          })}

          <Box
            mb={2.5}
            mt={2.5}
            sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'flex-end',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 260 },
                borderRadius: 999,
                px: 3,
                py: 1.35,
                fontWeight: 900,
                textTransform: 'none',
                boxShadow: 3,
              }}
            >
              {t('button')}
            </Button>
          </Box>

          <Accordion
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '16px !important',
              overflow: 'hidden',
              bgcolor: 'background.paper',
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel-content"
              id="panel-header"
              sx={{
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiAccordionSummary-content': {
                  my: 1.5,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${chartColours[1]}26`,
                    color: chartColours[1],
                    border: 1,
                    borderColor: `${chartColours[1]}55`,
                    flexShrink: 0,
                  }}
                >
                  <HelpOutlineOutlined sx={{ fontSize: 24 }} />
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 900,
                  }}
                >
                  {t('help1.title')}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails
              id="panel-content"
              sx={{
                display: 'block',
                p: { xs: 2, md: 2.5 },
                '& p': {
                  mt: 0,
                  mb: 1.5,
                  lineHeight: 1.65,
                },
                '& p:last-child': {
                  mb: 0,
                },
                '& ul, & ol': {
                  pl: 3,
                },
                '& li': {
                  mb: 0.75,
                  lineHeight: 1.6,
                },
                '& a': {
                  fontWeight: 900,
                },
              }}
            >
              <Typography variant="body1" component="div" color="text.secondary">
                <Markdown>{t('help1.content')}</Markdown>
              </Typography>
            </AccordionDetails>
          </Accordion>
        </form>
      </PageContainer>
    </MainLayout>
  );
}

export default TermsScene;
